import { humanizeEnum } from './labels';

/**
 * What Amazon SES told us about mail we sent, in words an admin reads.
 *
 * SES reports failures only. A send with no `deliveryStatus` is "no problem reported", which is
 * the closest the platform gets to "delivered"; it is labelled that way on purpose.
 *
 * Display only. Filters send the stored value, never the label.
 */

/** Send Log "Delivery" filter. `all` sends nothing; the rest are the backend's own values. */
export const DELIVERY_FILTER_OPTIONS = [
  { value: 'all', label: 'Any delivery' },
  { value: 'BOUNCED', label: 'Bounced' },
  { value: 'COMPLAINED', label: 'Complained' },
  { value: 'NONE', label: 'No problem' },
];

/** The query value for a filter state, or null when the filter is off or unrecognised. */
export const deliveryStatusParam = (filter: string | null | undefined): string | null => {
  const v = (filter ?? '').trim().toUpperCase();
  return v === 'BOUNCED' || v === 'COMPLAINED' || v === 'NONE' ? v : null;
};

export type DeliveryTone = 'error' | 'warning';

export interface DeliveryChip {
  label: string;
  tone: DeliveryTone;
}

interface DeliveryFields {
  deliveryStatus?: string | null;
  bounceType?: string | null;
}

/**
 * The chip for a send record, or null when SES reported nothing.
 *
 * A permanent bounce and a complaint are both final (the address is now blocked), so both read as
 * errors. A transient bounce is a mailbox that was full or a server that was down: a warning.
 */
export const deliveryChip = (r: DeliveryFields): DeliveryChip | null => {
  const status = (r.deliveryStatus ?? '').toUpperCase();
  if (status === 'COMPLAINED') return { label: 'Complained', tone: 'error' };
  if (status !== 'BOUNCED') return null;
  const type = (r.bounceType ?? '').toLowerCase();
  if (type === 'permanent') return { label: 'Bounced: permanent', tone: 'error' };
  if (type === 'transient') return { label: 'Bounced: transient', tone: 'warning' };
  return { label: 'Bounced', tone: 'error' };
};

/** Why an address is on the suppression list. */
export const SUPPRESSION_REASON_LABELS: Record<string, string> = {
  PERMANENT_BOUNCE: 'Hard bounce',
  COMPLAINT: 'Complaint',
  SES_LIST: 'Blocked by Amazon SES',
  SOFT_BOUNCE_REPEATED: 'Repeated soft bounces',
};

export const suppressionReasonLabel = (reason?: string | null): string => {
  const raw = (reason ?? '').trim();
  if (!raw) return 'Not recorded';
  return SUPPRESSION_REASON_LABELS[raw.toUpperCase()] ?? humanizeEnum(raw, 'Not recorded');
};

/** A complaint is the recipient's decision; the rest are the mailbox's. */
export const suppressionReasonTone = (reason?: string | null): DeliveryTone =>
  (reason ?? '').toUpperCase() === 'SOFT_BOUNCE_REPEATED' ? 'warning' : 'error';

export interface EmailSuppressionItem {
  email: string;
  reason: string;
  firstAt: string | null;
  lastAt: string | null;
  count: number;
  lastDiagnostic: string | null;
  source: string | null;
  userType: string | null;
  userId: string | null;
}

export interface EmailSuppressionPage {
  content: EmailSuppressionItem[];
  totalElements: number;
}

/** The BFF URL for one page of the suppression list. */
export const suppressionsUrl = (search: string, page: number, size: number): string => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  if (search.trim()) params.set('search', search.trim());
  return `/api/email-bounces/suppressions?${params.toString()}`;
};

/** The record page for the account behind an address, or null when there is none to open. */
export const suppressionAccountPath = (item: Pick<EmailSuppressionItem, 'userType' | 'userId'>): string | null => {
  const id = (item.userId ?? '').trim();
  if (!id) return null;
  const type = (item.userType ?? '').toLowerCase();
  if (type === 'jobseeker') return `/jobseekers/${encodeURIComponent(id)}`;
  if (type === 'employer') return `/employers/${encodeURIComponent(id)}`;
  return null;
};

export const suppressionAccountLabel = (userType?: string | null): string => {
  const type = (userType ?? '').toLowerCase();
  if (type === 'jobseeker') return 'Job seeker';
  if (type === 'employer') return 'Employer';
  return 'No account';
};

/** Where the platform learnt of it: a live SES event, or the one-off import of SES's own list. */
export const suppressionSourceLabel = (source?: string | null): string => {
  const v = (source ?? '').toUpperCase();
  if (v === 'SES_EVENT') return 'SES event';
  if (v === 'SES_LIST_IMPORT') return 'SES list import';
  return humanizeEnum(source, '');
};

export const SUPPRESSION_CSV_HEADER = [
  'Email', 'Reason', 'First blocked', 'Last event', 'Events', 'Last reason', 'Source', 'Account type', 'Account id',
];

export const suppressionCsvRow = (s: EmailSuppressionItem): unknown[] => [
  s.email,
  suppressionReasonLabel(s.reason),
  s.firstAt ?? '',
  s.lastAt ?? '',
  s.count,
  s.lastDiagnostic ?? '',
  suppressionSourceLabel(s.source),
  s.userId ? suppressionAccountLabel(s.userType) : '',
  s.userId ?? '',
];
